import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, message,
} from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

import { servicesAPI } from '../../api/client';


const DEFAULT_COLOR = '#6B7280';


export default function CategoryManagerModal({ open, onClose }) {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: categories, isLoading } = useQuery({
    queryKey: ['service-categories-management'],
    queryFn: () => servicesAPI.getCategories({ include_inactive: true }).then(r => r.data),
    enabled: open,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['service-categories'] });
    queryClient.invalidateQueries({ queryKey: ['service-categories-management'] });
    queryClient.invalidateQueries({ queryKey: ['services'] });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => (
      editing
        ? servicesAPI.updateCategory(editing.id, payload)
        : servicesAPI.createCategory(payload)
    ),
    onSuccess: () => {
      message.success(editing ? 'Категория обновлена' : 'Категория создана');
      refresh();
      setFormOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (error) => message.error(error?.response?.data?.detail || 'Ошибка'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, is_active }) => servicesAPI.updateCategory(id, { is_active }),
    onSuccess: (_, variables) => {
      message.success(variables.is_active ? 'Категория восстановлена' : 'Категория деактивирована');
      refresh();
    },
    onError: (error) => message.error(error?.response?.data?.detail || 'Ошибка'),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ color: DEFAULT_COLOR });
    setFormOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    form.setFieldsValue({ name: category.name, color: category.color || DEFAULT_COLOR });
    setFormOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    saveMutation.mutate({
      name: values.name.trim(),
      color: values.color || DEFAULT_COLOR,
    });
  };

  const columns = [
    {
      title: 'Категория',
      dataIndex: 'name',
      render: (value, row) => <Tag color={row.color}>{value}</Tag>,
    },
    {
      title: 'Сервисов',
      dataIndex: 'services_count',
      width: 90,
      align: 'center',
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      width: 110,
      render: (value) => value ? <Tag color="green">Активна</Tag> : <Tag>Неактивна</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Изменить
          </Button>
          <Popconfirm
            title={row.is_active ? 'Деактивировать категорию?' : 'Восстановить категорию?'}
            onConfirm={() => statusMutation.mutate({ id: row.id, is_active: !row.is_active })}
            okText="Да"
            cancelText="Нет"
          >
            <Button size="small" danger={row.is_active}>
              {row.is_active ? 'Отключить' : 'Восстановить'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        title="Категории сервисов"
        onCancel={onClose}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Новая категория
          </Button>
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={categories || []}
          columns={columns}
          pagination={false}
        />
      </Modal>

      <Modal
        open={formOpen}
        title={editing ? 'Редактировать категорию' : 'Новая категория'}
        onOk={save}
        onCancel={() => { setFormOpen(false); setEditing(null); form.resetFields(); }}
        okText={editing ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        confirmLoading={saveMutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, whitespace: true, message: 'Укажите название категории' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="color"
            label="Цвет"
            rules={[{ required: true, message: 'Укажите цвет категории' }]}
          >
            <Input type="color" style={{ width: 90, padding: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
